import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { Skeleton } from '@/components/ui/skeleton';
import StarRating from '@/components/coffee/StarRating';
import { Sparkles, Coffee as CoffeeIcon, MapPin, Quote } from 'lucide-react';

// Deterministic daily pick: same coffee for everyone for the whole day,
// changes to a new one each day.
function pickDaily(coffees) {
  if (!coffees.length) return null;
  const now = new Date();
  const dayNumber = Math.floor(
    Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()) / 86400000
  );
  return coffees[dayNumber % coffees.length];
}

export default function TodaysPickUp({ coffees, loading }) {
  const coffee = loading ? null : pickDaily(coffees);
  const [latestReview, setLatestReview] = useState(null);

  useEffect(() => {
    if (!coffee?.id) return;
    let active = true;
    api.entities.Review.filter({ coffee_id: coffee.id }, '-created_date', 1).then((rows) => {
      if (active) setLatestReview(rows.find((r) => r.comment) || null);
    });
    return () => { active = false; };
  }, [coffee?.id]);

  if (loading) {
    return (
      <section>
        <SectionHeader />
        <Skeleton className="h-96 md:h-48 rounded-2xl" />
      </section>
    );
  }

  if (!coffee) return null;

  const location = [coffee.cafe_name, coffee.cafe_area].filter(Boolean).join(' · ');

  return (
    <section>
      <SectionHeader />
      <Link to={`/coffee/${coffee.id}`} className="block group">
        <div className="bg-card rounded-2xl border border-border overflow-hidden hover:opacity-90 transition-opacity md:flex">
          {/* Photo */}
          <div className="relative w-full aspect-square md:aspect-auto md:w-56 md:h-auto shrink-0 bg-secondary">
            {coffee.photo_url ? (
              <img src={coffee.photo_url} alt={coffee.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <CoffeeIcon size={40} className="text-muted-foreground/40" strokeWidth={1} />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="p-4 md:p-5 flex-1 min-w-0 space-y-2">
            <h3 className="font-heading font-semibold text-lg leading-tight text-foreground">{coffee.name}</h3>
            {location && (
              <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <MapPin size={14} className="shrink-0" />
                <span className="truncate">{location}</span>
              </p>
            )}
            {coffee.average_rating > 0 && (
              <StarRating rating={coffee.average_rating} size={14} />
            )}
            {coffee.recipes && (
              <div className="pt-1">
                <p className="text-[10px] tracking-wide text-primary uppercase font-semibold mb-0.5">Recipe</p>
                <p className="text-sm text-foreground/80 leading-relaxed line-clamp-2">{coffee.recipes}</p>
              </div>
            )}
            {latestReview?.comment && (
              <div className="pt-2 mt-1 border-t border-border/50">
                <p className="flex items-start gap-1.5 text-sm text-foreground/70 italic leading-relaxed line-clamp-2">
                  <Quote size={13} className="shrink-0 mt-1 text-muted-foreground" />
                  <span>{latestReview.comment}</span>
                </p>
                <p className="text-[11px] text-muted-foreground mt-1 pl-[19px]">— {latestReview.reviewer_name || 'Anonymous'}</p>
              </div>
            )}
          </div>
        </div>
      </Link>
    </section>
  );
}

function SectionHeader() {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Sparkles size={16} className="text-primary" strokeWidth={2} />
      <h2 className="font-heading font-semibold text-base">Today's Pick Up</h2>
    </div>
  );
}